import { useEffect, useState } from "react";
import { mutate } from "swr";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import useToast from "~/components/ui/toast";
import { projectListPath, projectPath, updateProject } from "~/services/projects";

interface RenameProjectDialogProps {
  projectId: string;
  currentName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const RenameProjectDialog = ({
  projectId,
  currentName,
  open,
  onOpenChange,
}: RenameProjectDialogProps) => {
  const { showError } = useToast();
  const [draft, setDraft] = useState(currentName);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setDraft(currentName);
    }
  }, [open, currentName]);

  const handleSave = async () => {
    const name = draft.trim();
    if (!name || name === currentName) {
      onOpenChange(false);
      return;
    }
    setSaving(true);
    try {
      await updateProject(projectId, { name });
      await Promise.all([mutate(projectPath(projectId)), mutate(projectListPath())]);
      onOpenChange(false);
    } catch {
      showError("Failed to rename project. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename project</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Label htmlFor="rename-project-name">Name</Label>
          <Input
            id="rename-project-name"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                void handleSave();
              }
            }}
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || !draft.trim()}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
